import React, { useState, useRef, useEffect } from "react";

interface MenuContextType {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const MenuContext = React.createContext<MenuContextType | undefined>(undefined);

interface MenuProps {
  children: React.ReactNode;
  open?: boolean;
  handler?: (open: boolean) => void;
  placement?: "top" | "bottom" | "left" | "right" | "bottom-end" | "top-end";
}

export const Menu: React.FC<MenuProps> = ({
  children,
  open: controlledOpen,
  handler,
  placement: _placement = "bottom",
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = (value: boolean) => {
    if (!isControlled) {
      setInternalOpen(value);
    }
    if (handler) {
      handler(value);
    }
  };

  return (
    <MenuContext.Provider value={{ open, setOpen }}>
      <div className="relative inline-block">{children}</div>
    </MenuContext.Provider>
  );
};

interface MenuHandlerProps {
  children: React.ReactElement<any>;
}

export const MenuHandler: React.FC<MenuHandlerProps> = ({ children }) => {
  const context = React.useContext(MenuContext);
  if (!context) {
    throw new Error("MenuHandler must be used within a Menu");
  }

  const { open, setOpen } = context;

  return React.cloneElement(children, {
    onClick: (e: React.MouseEvent) => {
      setOpen(!open);
      if (children.props.onClick) {
        children.props.onClick(e);
      }
    },
  });
};

interface MenuListProps extends React.HTMLAttributes<HTMLUListElement> {
  children: React.ReactNode;
}

export const MenuList: React.FC<MenuListProps> = ({
  children,
  className = "",
  ...props
}) => {
  const context = React.useContext(MenuContext);
  if (!context) {
    throw new Error("MenuList must be used within a Menu");
  }

  const { open, setOpen } = context;
  const menuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, setOpen]);

  if (!open) return null;

  return (
    <ul
      ref={menuRef}
      className={`absolute z-50 min-w-[180px] overflow-auto rounded-lg border border-blue-gray-50 bg-white p-3 font-sans text-sm font-normal text-blue-gray-500 shadow-lg shadow-blue-gray-500/10 focus:outline-none ${className}`}
      style={{ top: "calc(100% + 4px)", right: 0 }}
      {...props}
    >
      {children}
    </ul>
  );
};

interface MenuItemProps extends React.HTMLAttributes<HTMLLIElement> {
  children: React.ReactNode;
}

export const MenuItem: React.FC<MenuItemProps> = ({
  children,
  className = "",
  ...props
}) => {
  return (
    <li
      role="menuitem"
      className={`block w-full cursor-pointer select-none rounded-md px-3 pt-[9px] pb-2 text-start leading-tight transition-all hover:bg-blue-gray-50 hover:bg-opacity-80 hover:text-blue-gray-900 focus:bg-blue-gray-50 focus:bg-opacity-80 focus:text-blue-gray-900 active:bg-blue-gray-50 active:bg-opacity-80 active:text-blue-gray-900 ${className}`}
      {...props}
    >
      {children}
    </li>
  );
};